import { useContext, useEffect, useRef, useState } from 'react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
  useEvaluatedAttributes,
} from '@ea-lab/reactive-json'

/**
 * Hook — animates a number from 0 to `target` on `duration` ms, started
 * when the element enters the viewport. The suffix (any non-digit chars)
 * is preserved (e.g. "20+" → animates "0" → "20" then displays "20+").
 */
const useCountUp = (target, duration = 1800) => {
  const [display, setDisplay] = useState('0')
  const ref = useRef(null)
  const animated = useRef(false)

  useEffect(() => {
    // Reset the lock on every target change. The component is re-used by the
    // Switch iteration when stats are edited, so the ref would otherwise stay
    // `true` from the initial animation and block any subsequent refresh.
    animated.current = false
    setDisplay('0')

    const numeric = parseInt(String(target).replace(/\D/g, ''), 10)
    if (isNaN(numeric)) { setDisplay(String(target ?? '')); return }

    const suffix = String(target).replace(/[0-9]/g, '')

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !animated.current) {
          animated.current = true
          const start = performance.now()
          const tick = (now) => {
            const elapsed = now - start
            const progress = Math.min(elapsed / duration, 1)
            const eased = 1 - Math.pow(1 - progress, 3)
            const current = Math.round(eased * numeric)
            setDisplay(`${current}${suffix}`)
            if (progress < 1) requestAnimationFrame(tick)
          }
          requestAnimationFrame(tick)
          observer.disconnect()
        }
      },
      { threshold: 0.3 }
    )

    if (ref.current) observer.observe(ref.current)
    return () => observer.disconnect()
  }, [target, duration])

  return { display, ref }
}

/**
 * ReactiveJSON primitive — renders a number that animates from 0 to its
 * target value when it enters the viewport.
 *
 * YAML usage:
 *   - type: CountUp
 *     value: ~.value           # the number (or "20+" / "1.2k" — non-digit suffix preserved)
 *     duration: 1800           # optional, ms
 *     tag: span                # optional, default "span"
 *     attributes:
 *       class: "text-5xl font-bold tabular-nums"
 */
export const CountUp = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const resolve = (v) =>
    evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const value = resolve(props.value)
  const duration = resolve(props.duration) ?? 1800
  const evaluatedAttrs = useEvaluatedAttributes(props.attributes ?? {})

  const { display, ref } = useCountUp(value, duration)

  const Tag = props.tag ?? 'span'

  return (
    <ActionDependant {...props}>
      <Tag ref={ref} {...evaluatedAttrs}>{display}</Tag>
    </ActionDependant>
  )
}
